/**
 * `Account` view — account-scoped settings, organized as a LEFT-NAV TABBED
 * area (ADR-0155 design pass), replacing the earlier single-scroll stack.
 * Mirrors the site `Settings` shell (200px sticky tab column + content pane,
 * `flex-col md:flex-row`, `?account_tab=<id>` deep-link).
 *
 * Tabs: Profile · Billing · Invoices · Team · Security · Notifications.
 * The old static `BillingPanel` (a duplicate Discovery card) is GONE — the
 * Billing tab renders the real `BillingTab` (current plan + picker) and the
 * Invoices tab renders `InvoicesSection`, both from the billing view.
 *
 * The account fetch (`/v1/account`) feeds the Profile + Notifications tabs;
 * Billing / Invoices / Team / Security self-fetch.
 */

import { Bell, Briefcase, CreditCard, Plug, Receipt, Shield, TriangleAlert, User as UserIcon, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { AccountWire } from '@sill/types/api';
import type { User } from '@sill/types';

import { useOptionalHttp } from '../../api/http-context.js';
import { useOptionalAgency } from '../../app/agency-context.js';
import { useAuth } from '../../app/auth-context.js';
import { useOptionalSites } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { Card, NumLabel } from '../../components/atoms/index.js';
import { getAccount } from '../../api/account.js';
import { BillingTab, InvoicesSection } from '../billing/Billing.js';
import { AgencyAccessPanel } from './AgencyAccessPanel.js';
import { ProfilePanel } from './ProfilePanel.js';
import { SecurityPanel } from './SecurityPanel.js';
import { NotificationsPanel } from './NotificationsPanel.js';
import { TeamPanel } from './TeamPanel.js';
import { ConnectedAppsPanel } from './ConnectedAppsPanel.js';
import { DangerPanel } from './DangerPanel.js';
import { LegalHoldPanel } from './LegalHoldPanel.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

type TabId =
  | 'profile'
  | 'billing'
  | 'invoices'
  | 'team'
  | 'agency_access'
  | 'connected_apps'
  | 'security'
  | 'notifications'
  | 'danger';

const TABS: readonly { id: TabId; label: string; icon: typeof UserIcon }[] = [
  { id: 'profile', label: 'Profile', icon: UserIcon },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'invoices', label: 'Invoices', icon: Receipt },
  { id: 'team', label: 'Team', icon: Users },
  // ADR-0237 §6 — "Agency access" is visible to every account. The
  // panel inside the tab lists agencies with access + a conditional
  // Agency add-on block (shown only when features.agency === true).
  { id: 'agency_access', label: 'Agency access', icon: Briefcase },
  // ADR-0261 — manage assistant (Claude / ChatGPT) connections against
  // this account. Account-level because the OAuth grant binds to the
  // user + home account, not any one site.
  { id: 'connected_apps', label: 'Connected apps', icon: Plug },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'danger', label: 'Danger', icon: TriangleAlert },
];

const TAB_IDS = new Set<TabId>(TABS.map((t) => t.id));

/** Read + consume the optional `?account_tab=<id>` deep-link. */
function readDeepLinkTab(): TabId | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = new URLSearchParams(window.location.search).get('account_tab');
    if (raw !== null && TAB_IDS.has(raw as TabId)) return raw as TabId;
  } catch {
    /* enhancement only */
  }
  return null;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; account: AccountWire }
  | { kind: 'error'; message: string };

export function Account(): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useAuth();
  // Optional — test scaffolds that render Account without an
  // AgencyProvider still work; `acting` is simply `null`.
  const agency = useOptionalAgency();
  // Optional — test scaffolds (jsdom RTL) often omit the SiteProvider.
  // The per-site deep-link list in NotificationsPanel just hides.
  const sites_ctx = useOptionalSites();
  const sites = sites_ctx?.sites ?? [];
  const acting = agency?.acting ?? null;
  const [tab, setTab] = useState<TabId>(() => readDeepLinkTab() ?? 'profile');
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [user, setUser] = useState<User | null>(
    auth.state.kind === 'authenticated' ? auth.state.user : null,
  );

  // ADR-0237 §3 — while acting, owner-only / money-moving panels are
  // hidden. The server already refuses the writes behind each (see
  // `ACTING_SESSION_DENIED_WRITES`); hiding the tabs stops a confused
  // agency admin from landing on a surface that would 403 every save.
  // Agency Access on an acting session would show the agencies with
  // access to this CLIENT — including, oddly, the acting agency
  // itself; hiding it keeps the surface honest ("your role is managing,
  // not being managed").
  const VISIBLE_TABS = useMemo<readonly { id: TabId; label: string; icon: typeof UserIcon }[]>(() => {
    if (acting === null) return TABS;
    const HIDDEN_WHILE_ACTING: ReadonlySet<TabId> = new Set([
      'billing',
      'invoices',
      'team',
      'agency_access',
      'danger',
      // User-scoped (your own sessions + email preferences): their writes go
      // to /v1/account/me + /v1/account/sessions, which are denied while
      // acting. Return to the agency to manage them.
      'security',
      'notifications',
      // ADR-0261 — assistant OAuth grants bind to the home session;
      // consent + revocation are refused for acting sessions server-
      // side, and listing a client's grants from an agency context
      // would be misleading. Hidden here to match.
      'connected_apps',
    ]);
    return TABS.filter((t) => !HIDDEN_WHILE_ACTING.has(t.id));
  }, [acting]);

  // Consume the deep-link param once so a manual tab change isn't overwritten
  // by a stale URL value on remount (mirrors Settings).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('account_tab')) {
        url.searchParams.delete('account_tab');
        window.history.replaceState(null, '', url.toString());
      }
    } catch {
      /* enhancement only */
    }
  }, []);

  useEffect(() => {
    if (auth.state.kind === 'authenticated') setUser(auth.state.user);
  }, [auth.state]);

  useEffect(() => {
    if (http === null) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    void (async () => {
      const result = await getAccount(http);
      if (cancelled) return;
      setState(
        result.kind === 'ok'
          ? { kind: 'ready', account: result.account }
          : { kind: 'error', message: result.message },
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [http]);

  const onAccountUpdated = useCallback((next: AccountWire): void => {
    setState((current) => (current.kind === 'ready' ? { ...current, account: next } : current));
  }, []);

  const onUserUpdated = useCallback(
    (next: User): void => {
      setUser(next);
      void auth.refresh();
    },
    [auth],
  );

  // Consolidated Notifications (founder brief 2026-10-20) — one patcher
  // merges any subset of `notification_preferences` keys into the
  // signed-in user. Keeps every opt-out switch in one place.
  const onNotificationPrefsPatched = useCallback(
    (patch: Record<string, boolean>): void => {
      setUser((current) =>
        current === null
          ? current
          : {
              ...current,
              notification_preferences: {
                ...(current.notification_preferences ?? {}),
                ...patch,
              },
            },
      );
    },
    [],
  );

  // Only Profile needs the /v1/account fetch (account name + role). Everything
  // else self-fetches or reads `user` from auth (available immediately), so
  // those tabs render without waiting on the account load.
  const needsAccount = tab === 'profile';
  const loadingAccount =
    needsAccount && (state.kind === 'loading' || user === null);

  // Fall back from a now-hidden tab to Profile when acting. The deep-
  // link reader may have left `tab` on a hidden id (billing / team /
  // danger) if the user bookmarked that URL on a home session and now
  // opened it while acting.
  useEffect(() => {
    const visible = new Set<TabId>(VISIBLE_TABS.map((t) => t.id));
    if (!visible.has(tab)) setTab('profile');
  }, [VISIBLE_TABS, tab]);

  // Clear "YOUR ACCOUNT" / "Your account." when acting — the agency
  // user is editing the CLIENT's account settings, not their own.
  const kicker = acting === null ? 'Account' : 'Client account';
  const heading =
    acting === null
      ? 'Your account.'
      : `${state.kind === 'ready' ? state.account.name : 'Client'} settings.`;
  const subheading =
    acting === null
      ? null
      : 'You are editing settings for the client you are acting inside. Your own profile stays with your agency.';

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="mb-8">
        <NumLabel n={1}>{kicker}</NumLabel>
        <h1
          style={{
            fontFamily: serifFont,
            fontSize: 'clamp(32px, 5vw, 44px)',
            lineHeight: 1,
            color: C.text,
            letterSpacing: -1.5,
          }}
        >
          {heading}
        </h1>
        {subheading !== null ? (
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.muted,
              marginTop: 10,
              maxWidth: 640,
              lineHeight: 1.5,
            }}
            data-testid="account-acting-subheading"
          >
            {subheading}
          </div>
        ) : null}
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Tab column */}
        <div className="md:flex-shrink-0" style={{ width: '100%', maxWidth: 200 }}>
          <div className="md:sticky md:top-24 space-y-1">
            {VISIBLE_TABS.map((t) => {
              const active = tab === t.id;
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
                  style={{
                    fontFamily: sansFont,
                    fontSize: 13,
                    fontWeight: 500,
                    borderRadius: 2,
                    background: active ? C.surface2 : 'transparent',
                    color: active ? C.text : C.muted,
                    border: `1px solid ${active ? C.border : 'transparent'}`,
                    cursor: 'pointer',
                  }}
                >
                  <Icon size={13} aria-hidden="true" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content pane */}
        <div className="flex-1 min-w-0">
          {loadingAccount ? (
            <Card className="p-5 sm:p-6">
              <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
                Loading account…
              </div>
            </Card>
          ) : null}

          {needsAccount && state.kind === 'error' ? (
            <Card className="p-5 sm:p-6">
              <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg, lineHeight: 1.5 }}>
                Couldn&apos;t load your account — {state.message}
              </div>
            </Card>
          ) : null}

          {tab === 'profile' && state.kind === 'ready' && user !== null ? (
            <ProfilePanel
              account={state.account}
              user={user}
              role={user.role}
              onAccountUpdated={onAccountUpdated}
              onUserUpdated={onUserUpdated}
            />
          ) : null}

          {tab === 'billing' ? <BillingTab /> : null}
          {tab === 'invoices' ? <InvoicesSection /> : null}
          {tab === 'team' ? <TeamPanel /> : null}
          {tab === 'agency_access' ? <AgencyAccessPanel /> : null}
          {tab === 'connected_apps' ? <ConnectedAppsPanel /> : null}
          {tab === 'security' ? <SecurityPanel /> : null}
          {tab === 'danger' ? (
            <>
              <LegalHoldPanel />
              <div style={{ height: 20 }} />
              <DangerPanel />
            </>
          ) : null}

          {tab === 'notifications' && user !== null ? (
            <NotificationsPanel
              notification_preferences={user.notification_preferences ?? undefined}
              onUpdated={onNotificationPrefsPatched}
              sites={sites.map((s) => ({
                site_id: s.site_id as unknown as string,
                domain: s.domain,
              }))}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
